/**
 * Global megamenu store + pointer guard.
 * Runs once per page; the window.__megamenuInit guard prevents
 * double-registration when the Shopify theme editor reloads sections.
 *
 * Supports both load timings:
 *  - script runs before Alpine (normal) → registers via alpine:init
 *  - script runs after Alpine (defer)   → registers directly on window.Alpine
 */
(function () {
  if (window.__megamenuInit) return;
  window.__megamenuInit = true;

  function register() {
    Alpine.store('megamenu', {
      activeMenu: null,
      _openTimer: null,
      _closeTimer: null,

      /**
       * Open a menu.
       * - If a menu is already visible  → switch immediately (no flash).
       * - If nothing is open yet        → wait 150 ms so quick nav sweeps
       *   don't flicker menus open.
       */
      openAfterDelay(handle) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
        if (this.activeMenu === handle) return;
        clearTimeout(this._openTimer);
        this._openTimer = null;
        if (this.activeMenu !== null) {
          this.activeMenu = handle; /* already open — switch at once */
        } else {
          this._openTimer = setTimeout(() => {
            this.activeMenu = handle;
            this._openTimer = null;
          }, 150);
        }
      },

      /** Open immediately (keyboard / focus navigation). */
      openImmediate(handle) {
        clearTimeout(this._openTimer);
        clearTimeout(this._closeTimer);
        this._openTimer = null;
        this._closeTimer = null;
        this.activeMenu = handle;
      },

      /** Cancel a pending close without closing. */
      cancelClose() {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      },

      /**
       * Schedule a close after 150 ms.
       * Only starts one timer at a time — repeated calls while the
       * cursor is moving are no-ops until the timer fires or is cancelled.
       */
      scheduleClose() {
        if (this._closeTimer) return;
        clearTimeout(this._openTimer);
        this._openTimer = null;
        this._closeTimer = setTimeout(() => {
          this.activeMenu = null;
          this._closeTimer = null;
        }, 150);
      },

      /** Close instantly (e.g. cursor leaves the viewport). */
      closeImmediate() {
        clearTimeout(this._openTimer);
        clearTimeout(this._closeTimer);
        this._openTimer = null;
        this._closeTimer = null;
        this.activeMenu = null;
      },
    });

    /**
     * Single global pointer guard.
     *
     * Uses `pointerover` (fires once each time the cursor enters an element,
     * not continuously while it sits inside one) and `element.closest()`
     * (walks up the DOM tree) so any descendant of a trigger or panel is
     * treated as part of that zone — no bounding-box maths needed.
     */
    document.addEventListener(
      'pointerover',
      (e) => {
        const store = Alpine.store('megamenu');
        /* Nothing open and no pending timer — still process the event
           so the very first hover can call openAfterDelay(). */

        const trigger = e.target.closest('[data-nav-handle]');
        const inPanel = !!e.target.closest('.megamenu-container, .megamenu-hover-bridge');

        if (trigger) {
          const handle = trigger.dataset.navHandle;
          if (handle === store.activeMenu) {
            store.cancelClose(); /* still on the same trigger */
          } else {
            store.openAfterDelay(handle); /* moved to a different trigger */
          }
        } else if (inPanel) {
          store.cancelClose(); /* inside the open panel */
        } else {
          store.scheduleClose(); /* outside all relevant zones */
        }
      },
      { passive: true },
    );

    /* Close instantly when the cursor leaves the browser viewport entirely */
    document.addEventListener(
      'pointerleave',
      () => {
        Alpine.store('megamenu').closeImmediate();
      },
      { passive: true },
    );

    /* Close megamenu on Escape key */
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') Alpine.store('megamenu').closeImmediate();
    });
  }

  if (window.Alpine) {
    register(); /* Alpine already initialised (defer path) */
  } else {
    document.addEventListener('alpine:init', register); /* Normal path */
  }
})();
